import { useTheme } from "@/src/styles/theme-provider";
import { Router } from "expo-router";
import {
  ArchiveBook,
  Calendar,
  Home2,
  Menu,
  People,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { Pressable, View } from "react-native";

import NavItem from "@/src/components/ui-kit/nav-bar/bottom-nav-button";
import { BlocksUser } from "@/src/models/user.model";
import AccountSwitcherSheet from "../../account/account-switcher";
import OrgSwitcherSheet from "../../account/organization-switch";
import MoreMenuModal from "../../action-modals/more-menu-modal";

type NavBarProps = Readonly<{
  isActive: (path: string) => boolean;
  user: BlocksUser | null;
  t: (key: string) => string;
  router: Router;
  onLogoutPress: () => void;
  isLoggingOut: boolean;
}>;

type HoverNavItemProps = Readonly<{
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onPress: () => void;
}>;

const HoverNavItem = ({ icon, label, active, onPress }: HoverNavItemProps) => {
  return (
    <Pressable
      onPress={onPress}
      className="flex-1 items-center web:hover:bg-primary-2 rounded-xl active:bg-primary-2"
    >
      <NavItem icon={icon} label={label} active={active} onPress={onPress} />
    </Pressable>
  );
};

const MobileBottomNav = ({
  isActive,
  user,
  t,
  router,
  isLoggingOut,
  onLogoutPress,
}: NavBarProps) => {
  const { themeVars } = useTheme();
  const [showMoreDrawer, setShowMoreDrawer] = useState(false);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);

  const primaryColor = themeVars["--color-primary-default"] as string;
  const mutedColor = themeVars["--color-slate-default"] as string;

  const navItems = [
    {
      icon: (
        <Home2
          size={24}
          color={isActive("/feeds") ? primaryColor : mutedColor}
        />
      ),
      label: "BTN_FEED",
      active: isActive("/feeds"),
      onPress: () => router.push("/feeds"),
    },
    {
      icon: (
        <ArchiveBook
          size={24}
          color={isActive("/news") ? primaryColor : mutedColor}
        />
      ),
      label: "BTN_NEWS",
      active: isActive("/news"),
      onPress: () => router.push("/news/latest-news"),
    },
    {
      icon: (
        <Calendar
          size={24}
          color={isActive("/events") ? primaryColor : mutedColor}
        />
      ),
      label: "BTN_EVENTS",
      active: isActive("/events"),
      onPress: () => router.push("/events"),
    },
    {
      icon: (
        <People
          size={24}
          color={isActive("/groups") ? primaryColor : mutedColor}
        />
      ),
      label: "BTN_GROUPS",
      active: isActive("/groups"),
      onPress: () => router.push("/groups"),
    },
    {
      icon: (
        <Menu size={24} color={showMoreDrawer ? primaryColor : mutedColor} />
      ),
      label: "BTN_MORE",
      active: showMoreDrawer,
      onPress: () => setShowMoreDrawer(true),
    },
  ];

  return (
    <View
      // `testID` is the only one of RN's props that reaches the DOM as an
      // attribute (data-testid) without a native meaning — +html.tsx pads this
      // element for the iPhone home indicator when running standalone.
      testID="pwa-safe-bottom"
      className="absolute bottom-0 left-0 right-0 bg-chalk border-t border-ash-3 flex-row items-center justify-around px-2 pt-1 pb-1"
    >
      {navItems.map((navItem) => (
        <HoverNavItem
          key={navItem.label}
          icon={navItem.icon}
          label={navItem.label}
          active={navItem.active}
          onPress={navItem.onPress}
        />
      ))}

      <MoreMenuModal
        visible={showMoreDrawer}
        onClose={() => setShowMoreDrawer(false)}
        t={t}
        router={router}
        user={user as any}
        isActive={isActive}
        isLoggingOut={isLoggingOut}
        onLogoutPress={onLogoutPress}
        onSwitchAccount={() => setShowAccountSwitcher(true)}
        onSwitchOrg={() => setShowOrgSwitcher(true)}
      />
      <AccountSwitcherSheet
        visible={showAccountSwitcher}
        onClose={() => setShowAccountSwitcher(false)}
      />
      <OrgSwitcherSheet
        visible={showOrgSwitcher}
        onClose={() => setShowOrgSwitcher(false)}
      />
    </View>
  );
};

export default MobileBottomNav;
